import axios from 'axios'

const SERVICE_WORKER_URL = '/eapc-push-sw.js'
const INSTALLATION_ID_KEY = 'eapc.web_push.installation_id'
const PUSH_MESSAGE_TYPE = 'EAPC_PUSH_NOTIFICATION'

let registrationPromise: Promise<ServiceWorkerRegistration | null> | undefined
let subscriptionPromise: Promise<boolean> | undefined

export type TWebPushStatus = 'unsupported' | 'denied' | 'default' | 'subscribed' | 'unavailable'

export interface IWebPushMessagePayload {
  title?: string
  body?: string
  url?: string
  tag?: string
  severity?: string
}

export interface IWebPushNotificationMessage {
  type: typeof PUSH_MESSAGE_TYPE
  payload: IWebPushMessagePayload
}

interface IEnsureWebPushRegistrationOptions {
  requestPermission?: boolean
}

interface IVapidPublicKeyResponse {
  publicKey: string
}

export async function ensureWebPushRegistration(
  locale?: string,
  options: IEnsureWebPushRegistrationOptions = {},
): Promise<TWebPushStatus> {
  if (!isWebPushSupported()) {
    return 'unsupported'
  }

  if (Notification.permission === 'denied') {
    return 'denied'
  }

  let permission: NotificationPermission = Notification.permission

  if (permission === 'default' && options.requestPermission !== false) {
    permission = await Notification.requestPermission()
  }

  if (permission !== 'granted') {
    return permission
  }

  const subscribed = await subscribeBrowser(locale)

  return subscribed ? 'subscribed' : 'unavailable'
}

export async function enableWebPushNotifications(locale?: string): Promise<TWebPushStatus> {
  if (!isWebPushSupported()) {
    return 'unsupported'
  }

  const permission = await Notification.requestPermission()

  if (permission === 'denied') {
    return 'denied'
  }

  if (permission !== 'granted') {
    return 'default'
  }

  const subscribed = await subscribeBrowser(locale)

  return subscribed ? 'subscribed' : 'unavailable'
}

export function isWebPushNotificationMessage(value: unknown): value is IWebPushNotificationMessage {
  if (value === null || typeof value !== 'object') {
    return false
  }

  const candidate = value as Partial<IWebPushNotificationMessage>

  return (
    candidate.type === PUSH_MESSAGE_TYPE &&
    candidate.payload !== null &&
    typeof candidate.payload === 'object'
  )
}

function isWebPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

async function subscribeBrowser(locale?: string): Promise<boolean> {
  if (!subscriptionPromise) {
    subscriptionPromise = doSubscribeBrowser(locale).finally(() => {
      subscriptionPromise = undefined
    })
  }

  return subscriptionPromise
}

async function doSubscribeBrowser(locale?: string): Promise<boolean> {
  const registration = await getServiceWorkerRegistration()

  if (!registration) {
    return false
  }

  const { data } = await axios.get<IVapidPublicKeyResponse>('/api/pushDevice/vapidPublicKey')
  const applicationServerKey = base64UrlToUint8Array(data.publicKey)
  const existingSubscription = await registration.pushManager.getSubscription()
  const subscription = await getCurrentSubscription(
    registration,
    existingSubscription,
    applicationServerKey,
  )

  await axios.post('/api/pushDevice/register', {
    platform: 'WEB',
    provider: 'WEB_PUSH',
    appCode: 'INTRANET',
    installationId: getInstallationId(),
    deviceToken: JSON.stringify(subscription.toJSON()),
    environment: environment(),
    locale,
    appVersion: APP_VERSION,
    osVersion: osLabel(),
    deviceModel: browserLabel(),
  })

  return true
}

async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!registrationPromise) {
    registrationPromise = navigator.serviceWorker
      .register(SERVICE_WORKER_URL, { scope: '/', updateViaCache: 'none' })
      .then(
        async (registration) => {
          await registration.update().catch(() => undefined)

          return navigator.serviceWorker.ready
        },
        () => null,
      )
  }

  return registrationPromise
}

async function getCurrentSubscription(
  registration: ServiceWorkerRegistration,
  existingSubscription: PushSubscription | null,
  applicationServerKey: ArrayBuffer,
): Promise<PushSubscription> {
  if (existingSubscription && subscriptionUsesKey(existingSubscription, applicationServerKey)) {
    return existingSubscription
  }

  if (existingSubscription) {
    await existingSubscription.unsubscribe().catch(() => undefined)
  }

  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey,
  })
}

function subscriptionUsesKey(
  subscription: PushSubscription,
  applicationServerKey: ArrayBuffer,
): boolean {
  const subscriptionKey = subscription.options.applicationServerKey

  if (!subscriptionKey) {
    return false
  }

  return arrayBufferEquals(subscriptionKey, applicationServerKey)
}

function arrayBufferEquals(left: ArrayBuffer, right: ArrayBuffer): boolean {
  if (left.byteLength !== right.byteLength) {
    return false
  }

  const leftView = new Uint8Array(left)
  const rightView = new Uint8Array(right)

  for (let index = 0; index < leftView.length; index++) {
    if (leftView[index] !== rightView[index]) {
      return false
    }
  }

  return true
}

function base64UrlToUint8Array(value: string): ArrayBuffer {
  const padding = '='.repeat((4 - (value.length % 4)) % 4)
  const base64 = `${value}${padding}`.replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(base64)
  const output = new Uint8Array(raw.length)

  for (let i = 0; i < raw.length; i++) {
    output[i] = raw.charCodeAt(i)
  }

  return output.buffer as ArrayBuffer
}

function getInstallationId(): string {
  const existing = window.localStorage.getItem(INSTALLATION_ID_KEY)

  if (existing) {
    return existing
  }

  const installationId =
    typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`

  window.localStorage.setItem(INSTALLATION_ID_KEY, installationId)

  return installationId
}

function environment(): 'SANDBOX' | 'PRODUCTION' {
  return window.location.hostname.includes('localhost') ||
    window.location.hostname.endsWith('.localhost')
    ? 'SANDBOX'
    : 'PRODUCTION'
}

function browserLabel(): string {
  if (navigator.userAgent.includes('Edg/')) {
    return 'Microsoft Edge'
  }

  if (navigator.userAgent.includes('Firefox/')) {
    return 'Firefox'
  }

  if (navigator.userAgent.includes('Chrome/')) {
    return 'Chrome'
  }

  if (navigator.userAgent.includes('Safari/')) {
    return 'Safari'
  }

  return 'Browser'
}

function osLabel(): string {
  const userAgent = navigator.userAgent

  if (/iPhone|iPad|iPod/.test(userAgent)) {
    const match = userAgent.match(/OS ([\d_]+)/)

    return match ? `iOS ${match[1].replace(/_/g, '.')}` : 'iOS'
  }

  if (userAgent.includes('Mac OS X')) {
    const match = userAgent.match(/Mac OS X ([\d_]+)/)

    return match ? `macOS ${match[1].replace(/_/g, '.')}` : 'macOS'
  }

  if (userAgent.includes('Windows NT 10.0')) {
    return 'Windows 10'
  }

  if (userAgent.includes('Windows NT 6.3')) {
    return 'Windows 8.1'
  }

  if (userAgent.includes('Windows NT 6.2')) {
    return 'Windows 8'
  }

  if (userAgent.includes('Windows NT 6.1')) {
    return 'Windows 7'
  }

  if (userAgent.includes('Android')) {
    const match = userAgent.match(/Android ([\d.]+)/)

    return match ? `Android ${match[1]}` : 'Android'
  }

  if (userAgent.includes('Linux')) {
    return 'Linux'
  }

  return 'Unknown'
}
